import { useState } from 'react';
import { receptionApi, type ChangeSetView } from '../api/client';
import { useData } from '../lib/useData';
import { ReviewDrawer } from './ReviewDrawer';
import { Button, Card, Notice } from './ui';

/** Why someone on People can't be a receptionist yet (Joey, Nov 3 2026: the Seat card used to be missing, with no reason). */
export type NoSeatYet = 'invite' | 'invited' | 'link' | 'elsewhere';

/**
 * The Seat card for someone who can't be a receptionist yet: the Console is in Stitch, so a
 * receptionist signs in to Stitch with their own extension. It says what to do first; the steps
 * themselves are in the Stitch sign-in card just above.
 */
export function SeatNotYet({ name, why }: { name: string; why: NoSeatYet }) {
  const first = name.split(/\s+/)[0] || name;
  const words: Record<NoSeatYet, string> = {
    invite: `${first} can be a receptionist once they sign in to Stitch with this extension. Invite them under Stitch sign-in first.`,
    invited: `${first} can be a receptionist once they accept their invitation and sign in to Stitch.`,
    link: `${first} signs in to Stitch without an extension. Link this extension to them under Stitch sign-in, then choose Receptionist.`,
    elsewhere: `${first} signs in to Stitch with a different extension. A receptionist works from the extension they sign in with: choose Receptionist on that extension’s page.`
  };
  return (
    <Card title="Seat">
      <div className="card-body section-stack">
        <div className="segmented" role="group" aria-label={`${name}’s seat`}>
          <button type="button" className="on" aria-pressed disabled>
            Standard
          </button>
          <button type="button" aria-pressed={false} disabled>
            Receptionist
          </button>
        </div>
        <span className="muted">{words[why]}</span>
      </div>
    </Card>
  );
}

/**
 * Settings → Reception: Add a receptionist (Joey, Nov 3 2026). The same seat change as the Seat card
 * on their page, reviewed before it applies; only people who sign in with their own extension.
 */
export function AddReceptionist({ domain, people, writable, onChanged }: { domain: string; people: { name: string; extension: string }[]; writable: boolean; onChanged: () => void }) {
  const [ext, setExt] = useState('');
  const [plan, setPlan] = useState<ChangeSetView | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const who = people.find((p) => p.extension === ext);

  const add = async () => {
    if (!who) return;
    setBusy(true);
    setNote(null);
    try {
      const r = await receptionApi.setSeat(domain, who.extension, 'receptionist');
      if (r.changeSet) setPlan(r.changeSet);
      else {
        setNote({ tone: 'ok', text: `${who.name} is a receptionist now. Their Console is in their menu.` });
        setExt('');
        onChanged();
      }
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="section-stack">
      {people.length > 0 && (
        <div className="reception-add">
          <div className="field">
            <label htmlFor="add-receptionist">Add a receptionist</label>
            <select id="add-receptionist" className="input" value={ext} disabled={!writable || busy} onChange={(e) => setExt(e.target.value)}>
              <option value="">Choose a person…</option>
              {people.map((p) => (
                <option key={p.extension} value={p.extension}>
                  {p.name} · {p.extension}
                </option>
              ))}
            </select>
          </div>
          <Button size="small" disabled={!writable || busy || !who} onClick={() => void add()}>
            Make receptionist
          </Button>
        </div>
      )}
      <small className="muted">
        {people.length
          ? 'Only people who sign in to Stitch with their own extension are listed. Invite anyone else from their page in People first.'
          : 'Nobody can be added yet: a receptionist signs in to Stitch with their own extension. Invite them from their page in People first.'}
      </small>
      {note && <Notice tone={note.tone}>{note.text}</Notice>}
      {plan && (
        <ReviewDrawer
          open
          tenant={domain}
          items={[]}
          planned={plan}
          onUnstage={() => undefined}
          onClose={(applied) => {
            setPlan(null);
            if (applied) {
              setExt('');
              onChanged();
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * A person's seat (1.13.0, docs/receptionist.md): Standard or Receptionist. Making someone a
 * receptionist is a change set (their extension takes queue calls, and Operators follows), shown
 * before it applies; the seat follows once it's applied. Only for people who sign in to Stitch.
 */
export function SeatCard({ domain, extension, name, writable, onChanged }: { domain: string; extension: string; name: string; writable: boolean; onChanged?: () => void }) {
  const [version, setVersion] = useState(0);
  const seat = useData(() => receptionApi.seat(domain, extension).catch(() => null), [domain, extension, version]);
  const [plan, setPlan] = useState<ChangeSetView | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const s = seat.data;
  if (!s?.inStitch) return null;
  const first = name.split(/\s+/)[0] || name;

  const change = async (next: 'standard' | 'receptionist', inOperators?: boolean) => {
    setBusy(true);
    setNote(null);
    try {
      const r = await receptionApi.setSeat(domain, extension, next, inOperators);
      if (r.changeSet) setPlan(r.changeSet);
      else {
        setVersion((v) => v + 1);
        onChanged?.();
        setNote({ tone: 'ok', text: next === 'receptionist' ? `${first} is a receptionist now. Their Console is in their menu.` : `${first} is back to a standard seat.` });
      }
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Seat">
      <div className="card-body section-stack">
        <div className="segmented" role="group" aria-label={`${name}’s seat`}>
          {(
            [
              ['standard', 'Standard'],
              ['receptionist', 'Receptionist']
            ] as const
          ).map(([k, label]) => (
            <button key={k} type="button" className={s.seat === k ? 'on' : ''} aria-pressed={s.seat === k} disabled={!writable || busy || s.seat === k} onClick={() => void change(k)}>
              {label}
            </button>
          ))}
        </div>
        <span className="muted">
          {s.seat === 'receptionist'
            ? `${first} has the Console: they answer calls and send them on to people, groups, park and voicemail, or ask someone by message while the caller holds.`
            : 'Their own phone, Messages and calls. A receptionist also gets the Console, for answering the main line and sending calls on.'}
        </span>
        {s.seat === 'receptionist' && (
          <label className="check">
            <input type="checkbox" checked={s.inOperators} disabled={!writable || busy} onChange={(e) => void change('receptionist', e.target.checked)} />
            <span>
              In the Operators group
              <small className="muted"> · rings when someone calls Operators. Every receptionist is in it unless you take them out.</small>
            </span>
          </label>
        )}
        {note && <Notice tone={note.tone}>{note.text}</Notice>}
      </div>
      {plan && (
        <ReviewDrawer
          open
          tenant={domain}
          items={[]}
          planned={plan}
          onUnstage={() => undefined}
          onClose={(applied) => {
            setPlan(null);
            setVersion((v) => v + 1);
            if (applied) onChanged?.();
          }}
        />
      )}
    </Card>
  );
}
